/* eslint-disable no-nested-ternary */

'use client';

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Loader2, CheckCircle, AlertCircle, EyeOff, Eye } from 'lucide-react';

import { Link, Navigate, useSearchParams } from 'react-router';
import { toast } from 'react-toastify';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../components/ui/form';
import Input from '../components/ui/input';
import { Alert, AlertDescription } from '../components/ui/alert';
import { DASHBOARD_PAGE, FORGOTPASS_PAGE, LOGIN_PAGE } from '../routes/routeConstant';
import { resetPassword } from '../utils/api';
import getErrorMessage from '../utils/error';
import useAuth from '../stores/useAuth';

// Form validation schema
const formSchema = z
  .object({
    newPassword: z.string(),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type FormValues = z.infer<typeof formSchema>;

export default function ResetPassPage() {
  const { isAuthenticated, checkAuth, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Initialize form
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  });

  // Extract token from URL on component mount
  useEffect(() => {
    const tokenParam = searchParams.get('token');
    setToken(tokenParam);

    if (!tokenParam) {
      setError('Invalid or missing reset token. Please request a new password reset link.');
    }
  }, [searchParams]);

  // Form submission handler
  const onSubmit = async (data: FormValues) => {
    if (!token) {
      setError('Invalid or missing reset token. Please request a new password reset link.');
      return;
    }

    const sendData = {
      token,
      newPassword: data.newPassword,
    };

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await resetPassword(sendData);
      if (response.success) {
        setIsSuccess(true);
        form.reset();
      }
      throw new Error(response.message);
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  if (loading && !isAuthenticated) {
    return (
      <div className="flex w-full h-dvh justify-center items-center">
        <div className="flex animate-pulse aspect-square size-32 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
          <img src="/assets/MSA-Logo.jpg" alt="MSA-Logo" className="rounded" />
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    toast.warning('You are already logged in', {
      position: 'top-right',
    });
    return <Navigate to={DASHBOARD_PAGE} />;
  }

  return (
    <div className="flex justify-center items-center min-h-screen p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl font-bold">Reset Password</CardTitle>
          <CardDescription>Enter your new password below to reset your account password.</CardDescription>
        </CardHeader>
        <CardContent>
          {isSuccess ? (
            <div className="space-y-4">
              <Alert className="bg-primary/10 border-primary">
                <CheckCircle className="h-4 w-4 mr-2" />
                <AlertDescription>Your password has been successfully reset.</AlertDescription>
              </Alert>
              <div className="flex justify-center">
                <Button asChild>
                  <Link to={LOGIN_PAGE}>Go to Login</Link>
                </Button>
              </div>
            </div>
          ) : error && !token ? (
            <div className="space-y-4">
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4 mr-2" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
              <div className="flex justify-center">
                <Button asChild>
                  <Link to={FORGOTPASS_PAGE}>Request New Reset Link</Link>
                </Button>
              </div>
            </div>
          ) : (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="newPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>New Password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showNewPassword ? 'text' : 'password'}
                            placeholder="******"
                            {...field}
                            className="pr-10"
                          />
                          <button
                            type="button"
                            onClick={toggleNewPasswordVisibility}
                            className="absolute inset-y-0 right-0 flex items-center px-3"
                          >
                            {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirm New Password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showConfirmPassword ? 'text' : 'password'}
                            placeholder="******"
                            {...field}
                            className="pr-10"
                          />
                          <button
                            type="button"
                            onClick={toggleConfirmPasswordVisibility}
                            className="absolute inset-y-0 right-0 flex items-center px-3"
                          >
                            {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4 mr-2" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <Button type="submit" className="w-full" disabled={isSubmitting || !token}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Resetting Password...
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </Button>
              </form>
            </Form>
          )}
        </CardContent>
        {!isSuccess && token && (
          <CardFooter className="flex justify-center">
            <Button variant="link" asChild>
              <Link to={LOGIN_PAGE}>Back to Login</Link>
            </Button>
          </CardFooter>
        )}
      </Card>
    </div>
  );
}
